import { useState, useRef, useEffect } from "react";
import { X, Send, MessageSquare, Smile, Move, Maximize2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface CareerCoachBotProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function CareerCoachBot({ isOpen, onToggle }: CareerCoachBotProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      text: "👋 Welcome to StaffAbility! I'm your Career Coach assistant. I can help with questions about our program, career opportunities, and employer partnerships. How can I assist you today?",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Dragging and resizing state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 384, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, startX: 0, startY: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 384, height: 600 });
  const [isMobile, setIsMobile] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  // Check for mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Handle mouse events for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        // Calculate movement from initial drag start point for smoother motion
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y; // Use normal Y movement (down is positive)
        
        // Calculate new position from the original starting position
        const newX = dragStart.startX + deltaX;
        const newY = dragStart.startY - deltaY; // Invert for bottom positioning
        
        // Get screen boundaries - keep header accessible while allowing movement
        // NOTE: Widget is positioned from bottom-right corner
        // Positive X moves RIGHT (away from right edge)
        // Positive Y moves UP (away from bottom edge)
        
        const minHeaderVisible = 150; // Minimum header width that must stay visible
        
        // X boundaries: prevent header from sliding off screen horizontally
        const maxX = size.width - minHeaderVisible; // Don't slide too far right (into screen)
        const minX = -(window.innerWidth - minHeaderVisible); // Don't slide too far left (off screen)
        
        // Y boundaries: prevent widget from going off screen vertically  
        const maxY = window.innerHeight - 200; // Don't slide too high up
        const minY = -50; // Don't slide too far down (below bottom)
        
        setPosition({
          x: Math.max(minX, Math.min(maxX, newX)),
          y: Math.max(minY, Math.min(maxY, newY))
        });
      }
      
      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        // Calculate new size with screen boundaries
        const maxWidth = window.innerWidth - Math.max(0, position.x);
        const maxHeight = window.innerHeight - 100; // Leave space for bottom margin
        
        setSize({
          width: Math.max(300, Math.min(maxWidth, resizeStart.width + deltaX)),
          height: Math.max(400, Math.min(maxHeight, resizeStart.height + deltaY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ 
      x: e.clientX, 
      y: e.clientY, 
      startX: position.x, 
      startY: position.y 
    });
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({ 
      x: e.clientX, 
      y: e.clientY, 
      width: size.width, 
      height: size.height 
    });
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 });
    setSize({ width: 384, height: 600 });
    setDragStart({ x: 0, y: 0, startX: 0, startY: 0 });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInputValue("");

    // Simulate bot response with more sophisticated predetermined responses
    setTimeout(() => {
      let botResponse = "";
      const lowerCaseInput = inputValue.toLowerCase();
      
      // Job/Career related questions
      if (lowerCaseInput.includes("job") || lowerCaseInput.includes("work") || lowerCaseInput.includes("career") || lowerCaseInput.includes("employment")) {
        if (lowerCaseInput.includes("find") || lowerCaseInput.includes("get") || lowerCaseInput.includes("how")) {
          botResponse = "At StaffAbility, we help you find meaningful employment through our extensive employer network. Our process starts with a skills assessment, then we match you with positions that align with your abilities and interests. We've placed over 5,000 candidates with a 83% retention rate! Would you like to know about our partner employers?";
        } else if (lowerCaseInput.includes("type") || lowerCaseInput.includes("kind") || lowerCaseInput.includes("what")) {
          botResponse = "We partner with employers across diverse industries including technology, healthcare, manufacturing, hospitality, customer service, and skilled trades. Many positions offer benefits, advancement opportunities, and on-the-job training. Our business partners value your unique perspectives and are committed to inclusive hiring practices.";
        } else {
          botResponse = "Our career pipeline connects justice-involved individuals with employers who value skills and potential. We've partnered with 200+ businesses committed to fair-chance hiring. Our employment specialists work with you throughout the process - from interview preparation to long-term career development.";
        }
      } 
      // Training related questions
      else if (lowerCaseInput.includes("training") || lowerCaseInput.includes("skill") || lowerCaseInput.includes("learn") || lowerCaseInput.includes("education")) {
        if (lowerCaseInput.includes("cost") || lowerCaseInput.includes("pay") || lowerCaseInput.includes("free")) {
          botResponse = "Our training programs are provided at no cost to participants! We believe in removing financial barriers to success. Our programs are funded through grants, corporate partnerships, and donations. We also assist with transportation costs and necessary equipment for training.";
        } else if (lowerCaseInput.includes("type") || lowerCaseInput.includes("what") || lowerCaseInput.includes("offer")) {
          botResponse = "We offer specialized training in high-demand fields like digital skills, healthcare support, commercial driving, construction trades, culinary arts, and customer service. Our programs include both technical skills and essential workplace competencies like communication, teamwork, and conflict resolution.";
        } else {
          botResponse = "Our skills development approach is comprehensive - combining technical training, professional development, and ongoing mentorship. Programs typically run 6-12 weeks with flexible scheduling options. Many include industry-recognized certifications that significantly boost earning potential.";
        }
      }
      // Support services
      else if (lowerCaseInput.includes("support") || lowerCaseInput.includes("help") || lowerCaseInput.includes("service") || lowerCaseInput.includes("assist")) {
        if (lowerCaseInput.includes("housing") || lowerCaseInput.includes("home") || lowerCaseInput.includes("live")) {
          botResponse = "Housing stability is crucial for career success. We partner with housing specialists to help secure stable, affordable housing. This includes transitional housing options, rental assistance programs, and connections to supportive housing providers. We can also assist with housing applications and understanding tenant rights.";
        } else if (lowerCaseInput.includes("transport") || lowerCaseInput.includes("car") || lowerCaseInput.includes("bus")) {
          botResponse = "Transportation should never block your path to employment. We provide transportation assistance through bus passes, ride-sharing credits, and in some areas, access to our van service. For long-term solutions, we can connect you with affordable vehicle programs and licensing assistance.";
        } else if (lowerCaseInput.includes("health") || lowerCaseInput.includes("mental") || lowerCaseInput.includes("counseling")) {
          botResponse = "We understand that wellbeing is essential for career success. Our health support includes connections to affordable healthcare, mental health services, substance recovery programs, and wellness resources. Our case managers can help navigate insurance options and find providers who understand your specific needs.";
        } else {
          botResponse = "Our holistic support extends beyond employment to address barriers you might face. This includes assistance with housing, transportation, healthcare, legal services, financial literacy, and family reunification. Our support is personalized to your specific situation and continues throughout your career journey.";
        }
      }
      // Employer related questions
      else if (lowerCaseInput.includes("employer") || lowerCaseInput.includes("hire") || lowerCaseInput.includes("business") || lowerCaseInput.includes("company")) {
        if (lowerCaseInput.includes("benefit") || lowerCaseInput.includes("why") || lowerCaseInput.includes("incentive")) {
          botResponse = "Employers partnering with StaffAbility gain access to motivated talent, potential tax credits (up to $9,600 per eligible hire), specialized training support, and enhanced workforce diversity. Our business partners report a 12.5x ROI and 83% retention rate with our placements - significantly above industry averages.";
        } else if (lowerCaseInput.includes("partner") || lowerCaseInput.includes("join") || lowerCaseInput.includes("become")) {
          botResponse = "Becoming an employer partner is simple! We customize our approach to your business needs. Our team will conduct a needs assessment, develop a tailored hiring plan, and provide ongoing support for both you and your new employees. We're committed to creating successful, lasting employment relationships.";
        } else {
          botResponse = "Our employer partners range from small businesses to major corporations across diverse industries. They all share a commitment to fair-chance hiring and recognize the value of our candidates' skills, perspective, and determination. We provide comprehensive support throughout the hiring process and beyond.";
        }
      }
      // Success stories
      else if (lowerCaseInput.includes("success") || lowerCaseInput.includes("story") || lowerCaseInput.includes("example")) {
        botResponse = "We've helped thousands of individuals transform their lives through meaningful employment. For example, Michael joined our program after incarceration, completed our IT training, and now works as a network specialist earning $68,000 annually. Sarah, after completing our healthcare pathway, secured a stable medical assistant position that provides benefits for her family. These are just two examples among many inspiring success stories!";
      }
      // About the model/white paper
      else if (lowerCaseInput.includes("model") || lowerCaseInput.includes("white paper") || lowerCaseInput.includes("framework") || lowerCaseInput.includes("approach")) {
        botResponse = "The StaffAbility Framework is detailed in our comprehensive white paper 'Breaking Down Career Barriers.' Our model integrates personalized support services, targeted skills development, and strategic employer partnerships to create sustainable career pathways. The white paper includes our implementation strategy, financial projections, and evidence-based practices. You can download it directly from our website!";
      }
      // General fallback
      else {
        botResponse = "Thank you for your interest! StaffAbility's mission is creating pathways to sustainable careers through our three-pillar approach: personalized support services, targeted skills development, and strategic employer partnerships. We've helped thousands rebuild their lives with meaningful employment. What specific aspect of our program would you like to learn more about?";
      }

      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        text: botResponse,
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prevMessages => [...prevMessages, botMessage]);
    }, 1000);
  };

  return (
    <div 
      ref={chatRef}
      className={cn(
        "fixed bg-white shadow-2xl rounded-2xl overflow-hidden z-50 transition-all duration-300 transform border border-blue-100",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        isMobile ? "flex flex-col" : ""
      )}
      style={{
        left: isMobile ? '10px' : `calc(100vw - ${size.width + 20}px + ${position.x}px)`,
        bottom: isMobile ? '60px' : `${100 + position.y}px`,
        right: isMobile ? '10px' : 'auto',
        top: isMobile ? '80px' : 'auto',
        width: isMobile ? 'auto' : `${size.width}px`,
        height: isMobile ? 'auto' : `${size.height}px`,
        minWidth: isMobile ? 'auto' : '300px',
        minHeight: isMobile ? '300px' : '400px',
        maxHeight: isMobile ? 'calc(100vh - 140px)' : 'none'
      }}
    >
      {/* Chat Header - Draggable on desktop only */}
      <div 
        className={cn(
          "bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 flex items-center justify-between select-none",
          !isMobile ? "cursor-move" : "cursor-default"
        )}
        onMouseDown={!isMobile ? handleDragStart : undefined}
      >
        <div className="flex items-center space-x-3">
          <div className="bg-white rounded-full p-1.5">
            <MessageSquare className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-base">Career Coach</h3>
            <p className="text-xs text-blue-100">StaffAbility Assistant</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!isMobile && <Move className="h-4 w-4 text-blue-200 opacity-60" />}
          {!isMobile && (
            <button 
              onClick={resetPosition}
              className="text-white hover:bg-blue-800 rounded-full p-1.5 transition-colors"
              aria-label="Reset position"
              title="Reset position and size"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          <button 
            onClick={onToggle}
            className="text-white hover:bg-blue-800 rounded-full p-1.5 transition-colors"
            aria-label="Close chat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Chat Messages */}
      <div className={cn(
        "p-3 overflow-y-auto bg-gradient-to-b from-blue-50 to-white",
        isMobile ? "flex-1" : "h-96"
      )}>
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={cn(
              "mb-4 max-w-[85%] p-3 rounded-lg shadow-sm",
              message.isBot 
                ? "bg-white text-gray-800 border border-blue-100 rounded-tl-none ml-0" 
                : "bg-blue-600 text-white rounded-tr-none ml-auto"
            )}
          >
            <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
            <p className="text-xs mt-1.5 opacity-60">
              {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </p>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Response Buttons - only show if there are messages */}
      {messages.length > 1 && (
        <div className="bg-blue-50 px-3 py-2 flex gap-2 overflow-x-auto">
          <button 
            onClick={() => {
              const text = "Tell me about job opportunities";
              // Create a user message immediately
              const userMessage: Message = {
                id: `user-${Date.now()}`,
                text,
                isBot: false,
                timestamp: new Date()
              };
              setMessages([...messages, userMessage]);
              
              // Trigger bot response with the quick response text
              const lowerCaseInput = text.toLowerCase();
              setTimeout(() => {
                let botResponse = "Our career pipeline connects justice-involved individuals with employers who value skills and potential. We've partnered with 200+ businesses committed to fair-chance hiring. Our employment specialists work with you throughout the process - from interview preparation to long-term career development.";
                
                const botMessage: Message = {
                  id: `bot-${Date.now()}`,
                  text: botResponse,
                  isBot: true,
                  timestamp: new Date()
                };
                
                setMessages(prevMessages => [...prevMessages, botMessage]);
              }, 1000);
            }}
            className="text-xs whitespace-nowrap bg-white text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            Job opportunities
          </button>
          <button 
            onClick={() => {
              const text = "What training programs do you offer?";
              // Create a user message immediately
              const userMessage: Message = {
                id: `user-${Date.now()}`,
                text,
                isBot: false,
                timestamp: new Date()
              };
              setMessages([...messages, userMessage]);
              
              // Trigger bot response with the quick response text
              setTimeout(() => {
                let botResponse = "We offer specialized training in high-demand fields like digital skills, healthcare support, commercial driving, construction trades, culinary arts, and customer service. Our programs include both technical skills and essential workplace competencies like communication, teamwork, and conflict resolution.";
                
                const botMessage: Message = {
                  id: `bot-${Date.now()}`,
                  text: botResponse,
                  isBot: true,
                  timestamp: new Date()
                };
                
                setMessages(prevMessages => [...prevMessages, botMessage]);
              }, 1000);
            }}
            className="text-xs whitespace-nowrap bg-white text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            Training programs
          </button>
          <button 
            onClick={() => {
              const text = "Tell me about your support services";
              // Create a user message immediately
              const userMessage: Message = {
                id: `user-${Date.now()}`,
                text,
                isBot: false,
                timestamp: new Date()
              };
              setMessages([...messages, userMessage]);
              
              // Trigger bot response with the quick response text
              setTimeout(() => {
                let botResponse = "Our holistic support extends beyond employment to address barriers you might face. This includes assistance with housing, transportation, healthcare, legal services, financial literacy, and family reunification. Our support is personalized to your specific situation and continues throughout your career journey.";
                
                const botMessage: Message = {
                  id: `bot-${Date.now()}`,
                  text: botResponse,
                  isBot: true,
                  timestamp: new Date()
                };
                
                setMessages(prevMessages => [...prevMessages, botMessage]);
              }, 1000);
            }}
            className="text-xs whitespace-nowrap bg-white text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            Support services
          </button>
        </div>
      )}
      
      {/* Chat Input */}
      <form onSubmit={handleSendMessage} className="border-t p-3 flex items-center bg-white">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Type your message..."
            className="w-full p-2.5 pl-4 border rounded-l-full focus:outline-none focus:ring-2 focus:ring-blue-300 pr-10"
          />
          <button 
            type="button" 
            className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
            aria-label="Add emoji"
          >
            <Smile className="h-5 w-5" />
          </button>
        </div>
        <button 
          type="submit" 
          className="bg-blue-600 text-white p-2.5 rounded-r-full hover:bg-blue-700 transition-colors"
          disabled={!inputValue.trim()}
          aria-label="Send message"
        >
          <Send className="h-5 w-5" />
        </button>
      </form>
      
      {/* Center Button at bottom - desktop only */}
      {!isMobile && (
        <div className="bg-gray-50 px-3 py-2 flex justify-center border-t border-gray-100">
          <button
            onClick={resetPosition}
            className="text-xs text-gray-500 hover:text-blue-600 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
            title="Center widget position"
          >
            Center
          </button>
        </div>
      )}
      
      {/* Resize Handle - desktop only */}
      {!isMobile && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-blue-600 opacity-50 hover:opacity-75 transition-opacity"
          onMouseDown={handleResizeStart}
          style={{
            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)'
          }}
          title="Drag to resize"
        >
          <Maximize2 className="h-3 w-3 text-white absolute bottom-0.5 right-0.5" />
        </div>
      )}
    </div>
  );
}